import dayjs from "dayjs";
import styled from "styled-components";
import { ErrorBoundary } from "react-error-boundary";
import React, { useState, useCallback, useEffect } from "react";
import {
  Col,
  Row,
  List,
  Tabs,
  Card,
  Modal,
  Empty,
  Space,
  Avatar,
  Button,
  Skeleton,
  Typography,
  DatePicker,
  ConfigProvider,
} from "antd";

import ErrorFallback from "components/ErrorFallback";
import type { RangePickerProps } from "antd/es/date-picker";
import { ApiErrorTableComponent } from "components/ErrorHandler";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { AuditParams, AuditDTO } from "pages/Audit/models";
import { setArchiveAuditModalOpen } from "pages/Audit/auditSlice";
import { getFullName, getFullNameInitials, openNotificationWithIcon } from "utils";
import { useGetAuditQuery, useArchiveAuditTableMutation } from "pages/Audit/auditApiSlice";

const { RangePicker } = DatePicker;

interface IRangePickerProps {
  startDate?: string;
  endDate?: string;
  onOpen?: boolean;
}

const AuditListContainer = styled.div`
  .ant-table-content {
    border-radius: 4px;
  }

  colgroup {
    border-radius: 4px;
    col {
      border-radius: 4px;
    }
  }
`;

const AuditList: React.FC = () => {
  const count = 1;
  const dispatch = useAppDispatch();
  const [auditList, setAuditList] = useState<AuditDTO[]>();
  const [activeKey, setActiveKey] = useState<string | number>("1");
  const { isArchiveAuditModalOpen } = useAppSelector((state) => state.audit);

  const [rangePickerProps, setRangePickerProps] = useState<IRangePickerProps>({
    startDate: "",
    endDate: "",
  });
  const [archivePickerProps, setArchivePickerProps] = useState<IRangePickerProps>({
    startDate: "",
    endDate: "",
  });

  const [auditParams, setAuditParams] = useState<AuditParams>({
    page: 0,
    size: 10,
  });
  const [archiveAudit, archiveAuditResult] = useArchiveAuditTableMutation();
  const audits = useGetAuditQuery({
    ...auditParams,
    page: auditParams.page,
    size: auditParams.size,
  });

  const onLoadMore = useCallback(() => {
    setAuditList(
      audits.data?.response.content.concat([...new Array(count)].map(() => ({ loading: true }))),
    );

    setAuditParams({
      ...auditParams,
      size: auditParams.size && auditParams.size + 3,
    });

    window.dispatchEvent(new Event("resize"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audits.data?.response.content]);

  const handleFilterChange = (
    name: string = "",
    value: {
      key?: string;
      label?: string;
      value?: string;
      disabled?: boolean;
    },
    // event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setAuditParams((prevParams) => ({
      ...prevParams,
      [name]: value.value,
    }));
  };

  const handleArchiveAudit = () => {
    archiveAudit({
      archiveStartDate: archivePickerProps.startDate,
      archiveEndDate: archivePickerProps.endDate,
    })
      .unwrap()
      .then((payload) => {
        openNotificationWithIcon(
          "successful",
          `archived ${payload.response} successfully`,
          "success",
        );

        setActiveKey("1");
        setArchivePickerProps({ startDate: "", endDate: "" });
      })
      .catch((error) => {
        if (error.originalStatus >= 500 && error.originalStatus < 600) {
          openNotificationWithIcon(error.status, error.data, "error");
        } else if (error?.status === 400) {
          error.data?.errorMessageList?.length
            ? error.data?.errorMessageList?.forEach((errorMessage: string) =>
                openNotificationWithIcon(error.data.errorMessage, errorMessage, "warning"),
              )
            : openNotificationWithIcon(error.data.errorMessage, "", "warning");
        }
      })
      .finally(() => {
        dispatch(setArchiveAuditModalOpen(false));
      });
  };

  useEffect(() => {
    if (rangePickerProps?.onOpen) {
      handleFilterChange("initiationStartDate", {
        value: rangePickerProps.startDate,
      });
      handleFilterChange("initiationEndDate", {
        value: rangePickerProps.endDate,
      });
    }
  }, [rangePickerProps]);

  useEffect(() => {
    setAuditList(audits.data?.response.content);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audits.isLoading]);

  useEffect(() => {
    setAuditList(audits.data?.response.content);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audits.data?.response.content]);

  const loadMore =
    !audits.isLoading && !audits.isFetching ? (
      <div
        style={{
          height: 32,
          marginTop: 12,
          lineHeight: "32px",
          textAlign: "center",
        }}
      >
        <Button onClick={onLoadMore} disabled={!audits?.data?.response?.content.length}>
          loading more
        </Button>
      </div>
    ) : null;

  const disabledAuditDate: RangePickerProps["disabledDate"] = (current) => {
    return current && current > dayjs().endOf("day");
  };

  const disabledArchiveDate: RangePickerProps["disabledDate"] = (current) => {
    return current && current > dayjs().startOf("day");
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ConfigProvider
        renderEmpty={() =>
          audits.isError ? (
            <ApiErrorTableComponent queryResult={audits} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )
        }
      >
        <AuditListContainer>
          <Tabs
            type="card"
            defaultActiveKey="1"
            onChange={(activeKey) => setActiveKey(activeKey)}
            tabBarExtraContent={
              activeKey === "1" || activeKey === 1 ? (
                <RangePicker
                  size="large"
                  format="YYYY-MM-DD"
                  disabledDate={disabledAuditDate}
                  style={{
                    marginBottom: 0,
                    marginTop: -10,
                  }}
                  onOpenChange={(value) => {
                    setRangePickerProps((prev) => ({ ...prev, onOpen: value }));
                  }}
                  onChange={(dates, dateString) =>
                    setRangePickerProps((prev) => ({
                      ...prev,
                      startDate: dateString[0],
                      endDate: dateString[1],
                    }))
                  }
                />
              ) : null
            }
          >
            <Tabs.TabPane tab="Audit" key={1}>
              <List
                loadMore={loadMore}
                dataSource={auditList}
                itemLayout="horizontal"
                loading={audits.isFetching}
                className="demo-loadmore-list"
                renderItem={(item) => (
                  <List.Item actions={[]}>
                    <Skeleton avatar title={false} loading={item.loading} active>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            style={{
                              color: "#f56a00",
                              backgroundColor: "#fde3cf",
                            }}
                          >
                            {getFullNameInitials(item.initiatedBy)}
                          </Avatar>
                        }
                        title={<div> </div>}
                        description={
                          <Space>
                            <Typography.Text>
                              <span style={{ color: "brown" }}>{item.operation}</span> operation
                              initated by{" "}
                              <span style={{ color: "brown" }}>
                                {getFullName(item.initiatedBy)}
                              </span>{" "}
                              at{" "}
                              <span style={{ color: "green" }}>
                                {dayjs(item.initiatedOn).format("DD-MM-YYYY, h:mm a")}
                              </span>
                            </Typography.Text>
                          </Space>
                        }
                      />
                    </Skeleton>
                  </List.Item>
                )}
              />
            </Tabs.TabPane>

            <Tabs.TabPane tab="Archive" key={2}>
              <Row gutter={12} justify="start">
                <Col span={14}>
                  <Card
                    bordered={false}
                    bodyStyle={{
                      display: "flex",
                      marginBottom: 200,
                      flexDirection: "column",
                    }}
                  >
                    <Typography.Paragraph style={{ marginBottom: 30 }}>
                      Archive audit table using a date range so that the audit information on that
                      date range will be archived to the history table and deleted from the daily
                      audit table
                    </Typography.Paragraph>
                    <RangePicker
                      size="large"
                      format="YYYY-MM-DD"
                      placement="bottomLeft"
                      disabledDate={disabledArchiveDate}
                      style={{
                        marginTop: -10,
                        marginBottom: 30,
                        justifySelf: "center",
                      }}
                      onOpenChange={(value) => {
                        setArchivePickerProps((prev) => ({
                          ...prev,
                          onOpen: value,
                        }));
                      }}
                      onChange={(dates, dateString) =>
                        setArchivePickerProps((prev) => ({
                          ...prev,
                          endDate: dateString[1],
                          startDate: dateString[0],
                        }))
                      }
                    />

                    <Button
                      size="large"
                      type="primary"
                      style={{ width: 100, marginLeft: "auto" }}
                      disabled={!archivePickerProps.startDate && !archivePickerProps.endDate}
                      onClick={() => dispatch(setArchiveAuditModalOpen(true))}
                    >
                      Archive
                    </Button>
                  </Card>
                </Col>
              </Row>
            </Tabs.TabPane>
          </Tabs>

          <Modal
            okText="Yes"
            maskClosable={false}
            title="Archive Audit Table"
            visible={isArchiveAuditModalOpen}
            onOk={() => handleArchiveAudit()}
            closable={!archiveAuditResult.isLoading}
            confirmLoading={archiveAuditResult.isLoading}
            onCancel={() => dispatch(setArchiveAuditModalOpen(false))}
            cancelButtonProps={{
              disabled: archiveAuditResult.isLoading,
            }}
          >
            <Typography.Text style={{ fontSize: "14px" }}>
              Are you sure you want to archive audit table from {archivePickerProps.startDate} to{" "}
              {archivePickerProps.endDate}. Please verify as this is almost an irreversable process
            </Typography.Text>
          </Modal>
        </AuditListContainer>
      </ConfigProvider>
    </ErrorBoundary>
  );
};

export default AuditList;
